"use client";

import {
  useState,
  type DragEvent as ReactDragEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import Link from "next/link";
import { studyTaskHref } from "@/lib/study-task-link";
import { useDraggable } from "@dnd-kit/core";
import { CalendarCheck, MoreHorizontal } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { STUDY_PLAN_LABELS } from "@/lib/study-plan";
import type { ScheduledTask } from "@/lib/study-plan-state";
import {
  CODE_LANG_BADGE,
  PLAN_STRIPE,
  codeLangLabel,
  formatMonthDayWeekday,
  taskEmoji,
} from "./schedule-shared";

export interface TaskCardActions {
  onToggle: (task: ScheduledTask, done: boolean) => void;
  onSkip: (task: ScheduledTask, skipped: boolean) => void;
  onMove: (task: ScheduledTask, toISO: string | null) => void;
}

/** "⋯ → 다른 날로 이동" 액션시트에 띄울 후보 날짜(윈도우 주변 ~11일). 키보드/모바일 DnD 대안. */
export interface MoveTarget {
  iso: string;
  label: string;
  isToday: boolean;
}

/**
 * 학습 일정 task 카드 (JCG-2310).
 *
 * **두 가지 레이아웃 변형** (회귀 방지: 기본은 2단 title-hero, `inline` 일 때만 단일 행):
 *
 * 1. **기본 — 제목 우선(title-hero) 2단 레이아웃** (주 뷰·모바일 아젠다). 좁은 칼럼(~150–190px)에서도
 *    어떤 페이지인지 한눈에 읽히도록 제목이 카드 전체 폭을 쓰고, 길면 한글 단어 단위(`break-keep`)로 줄바꿈.
 *     ┌ plan 스트라이프 │ ─────────────────────────── ┐
 *     │                 │ [체크박스] [이모지]   [⋯]  │  ← 컨트롤 행(고정 높이, 작음)
 *     │                 │ 관계데이터모델 (전체 폭, 줄바꿈)│  ← 제목 행(hero, line-clamp-3)
 *     │                 │ [출석]                       │  ← 완료/출석 행(선택)
 *     └─────────────────┴─────────────────────────────┘
 *
 * 2. **`inline` — 단일 행(인라인) 레이아웃** (일자 상세 모달). 폭 넓은 단일 칼럼이라 제목을 컨트롤과
 *    같은 행에 두고 1줄 truncate 로 컴팩트하게.
 *     ┌ plan 스트라이프 │ [체크박스] [이모지] 제목(1줄, …) ……… [출석] [⋯] ┐
 *     └─────────────────┴───────────────────────────────────────────────┘
 *
 * - draggable=true(주 뷰 데스크탑·일자 모달): `useDraggable` 로 카드 **본문 전체**가 드래그 핸들.
 *   별도 그립 핸들 없음(제목에 가로폭 양보). `PointerSensor` 의 6px activationConstraint 가
 *   클릭(제목 링크)·체크박스 탭과 드래그를 분리한다. 컨트롤(체크박스·링크·⋯)에는 드래그
 *   리스너를 붙이지 않으므로 그 위에서의 포인터는 평소대로 동작한다.
 * - draggable=false(모바일 아젠다): 정적 카드.
 * - "⋯" 메뉴(완료·건너뛰기·다른 날로 이동·정규 날짜 복귀)가 드래그의 키보드/터치 대안.
 *
 * 색: plan 색 좌측 스트라이프(이론=blue/코딩=green) + 디자인 토큰 본문 + amber 체크박스.
 * done = 체크 + 취소선 톤, skipped = 흐림 + 취소선, attendance done = "출석" 미니 뱃지.
 */
export default function TaskCard({
  task,
  actions,
  draggable = false,
  moveTargets = [],
  inline = false,
  overlay = false,
  readOnly = false,
}: {
  task: ScheduledTask;
  actions: TaskCardActions;
  draggable?: boolean;
  moveTargets?: MoveTarget[];
  /** 단일 행(인라인) 레이아웃. 일자 상세 모달처럼 폭 넓은 칼럼에서 사용. 기본은 2단 title-hero. */
  inline?: boolean;
  /** DragOverlay 안에서 그릴 떠 있는 클론(들어올림 스타일, 드래그 핸들 없음). */
  overlay?: boolean;
  /** 이전 회차 조회 모드. 제목 링크와 저장된 상태만 보여주고 모든 변경 동작을 제거한다. */
  readOnly?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const canDrag = draggable && !readOnly;

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    // overlay 클론은 소스와 dnd-kit id 충돌을 피하려 별도 id(어차피 draggable=false → disabled).
    id: overlay ? `overlay:${task.uid}` : task.uid,
    data: { task },
    disabled: !canDrag,
  });

  const handleToggle = async () => {
    if (busy || readOnly) return;
    setBusy(true);
    try {
      await actions.onToggle(task, !task.done);
    } finally {
      setBusy(false);
    }
  };

  // 저장된 완료 날짜가 자리를 정하는 항목은 "완료한 날짜 되돌리기"(완료 날짜 삭제)가 먼저 뜨고,
  // 그 뒤 예정 날짜 이동이 남아 있으면 "정규 날짜로 되돌리기"가 뜬다(JCG-3547, JCG-3653).
  const moved = task.completionDrivesDate || task.movedToDate !== null;
  const checkboxId = `task-${task.uid}`;
  const dimmed = task.done || task.skipped;
  const showAttendance = task.attendanceRecorded && task.done && !task.skipped;

  // 다국어 코딩 task 는 같은 제목("함수")이 C/Java/Python 3개로 나란히 뜨므로 언어 뱃지로 구분(JCG-2658).
  // 빈 값(이론·단일언어·관련링크)은 뱃지 없이 기존과 100% 동일.
  const langLabel = codeLangLabel(task.codeLang);
  const reviewLabel = task.reviewRound ? `${task.reviewRound}회독` : "";
  // 같은 제목의 언어·회독을 체크박스와 링크에서도 구분한다.
  const titleWithDetails = [reviewLabel, task.title, langLabel]
    .filter(Boolean)
    .join(" ");

  // 두 레이아웃(2단 title-hero / inline 단일 행)이 공유하는 컨트롤·뱃지·제목 조각.
  // 구조(행 배치)만 변형별로 다르고 내부 요소·핸들러·a11y 는 동일하게 유지한다(회귀 방지).
  const checkbox = (
    <Checkbox
      id={checkboxId}
      checked={task.done}
      disabled={readOnly || busy || task.skipped}
      onCheckedChange={readOnly ? undefined : handleToggle}
      aria-label={
        readOnly
          ? `${titleWithDetails} ${task.done ? "완료" : task.skipped ? "건너뜀" : "미완료"}, 읽기 전용`
          : `${titleWithDetails} 완료 표시`
      }
      className="relative shrink-0 border-amber-400 before:absolute before:-inset-2 before:content-[''] data-[state=checked]:border-amber-500 data-[state=checked]:bg-amber-500 dark:data-[state=checked]:bg-amber-400 dark:data-[state=checked]:text-amber-950"
    />
  );

  const emoji = (
    <span className="shrink-0 text-sm leading-none" aria-hidden>
      {taskEmoji(task)}
    </span>
  );

  // 언어 뱃지(중립 톤). 정보는 aria-label(titleWithDetails)이 보존하므로 뱃지는 aria-hidden.
  const langBadge = langLabel ? (
    <span className={CODE_LANG_BADGE} aria-hidden>
      {langLabel}
    </span>
  ) : null;

  const attendanceBadge = showAttendance ? (
    <span className="flex shrink-0 items-center gap-0.5 text-[10px] font-semibold text-green-600 dark:text-green-400">
      <CalendarCheck className="h-3 w-3" aria-hidden />
      출석
    </span>
  ) : null;

  const menu = readOnly ? null : (
    <TaskMenu
      task={task}
      actions={actions}
      moveTargets={moveTargets}
      moved={moved}
    />
  );

  // 제목 링크. inline=true 면 1줄 truncate, 아니면 최대 3줄 한글 줄바꿈(hero).
  // <Link> 텍스트는 항상 전체 제목 그대로(truncate 는 시각만 자름, a11y 보존).
  const titleLink = (
    <Link
      href={studyTaskHref(task, readOnly)}
      aria-label={titleWithDetails}
      className={cn(
        "rounded text-[13px] font-medium leading-snug transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500",
        inline
          ? "min-w-0 flex-1 truncate"
          : // 한글이 한 글자로 잘리지 않도록: 단어(어절) 단위 줄바꿈 + 정상 줄바꿈 + 3줄 클램프
            "whitespace-normal break-keep [overflow-wrap:anywhere] line-clamp-3",
        dimmed
          ? "text-muted-foreground line-through decoration-amber-500/50"
          : "text-foreground hover:text-amber-600 dark:hover:text-amber-400",
      )}
    >
      {reviewLabel && (
        <span className={cn(CODE_LANG_BADGE, "mr-1 align-middle")} aria-hidden>
          {reviewLabel}
        </span>
      )}
      {task.title}
    </Link>
  );

  // 본문 전체를 드래그 핸들로 사용(데스크탑). attributes 의 기본 tabIndex 를 -1 로 덮어
  // 키보드 탭 스톱 중복을 피한다(키보드 이동 대안은 ⋯ 메뉴가 담당). 카드 자체에는 의미
  // 정보가 없고 내부 체크박스/링크/메뉴가 각자 역할을 가지므로 aria-hidden 처리하지 않는다.
  // 카드 안 제목은 <Link>(=<a>) 라 브라우저 native 드래그(URL 고스트)가 dnd-kit pointer 드래그를
  // 가로챈다. onDragStart 에서 preventDefault 로 native 드래그를 막아, 카드 어디를 잡아도
  // dnd-kit 드래그가 동작하게 한다(dnd-kit 은 pointer 이벤트라 영향 없음).
  const dragHandleProps = canDrag
    ? {
        ...attributes,
        ...listeners,
        tabIndex: -1,
        onDragStart: (e: ReactDragEvent<HTMLDivElement>) => e.preventDefault(),
      }
    : {};

  return (
    <div
      ref={canDrag ? setNodeRef : undefined}
      // 드롭 위치 탐지용 마커 — 부모의 DnD 로직(schedule-dnd freezeColumns/dropTargetFrozen)이
      // 이 마커로 카드 위치를 스냅샷하고 커서와 비교해 같은 칼럼 내 삽입 위치(정렬값)를 계산한다.
      data-task-uid={task.uid}
      className={cn(
        "group/card relative flex items-stretch gap-1.5 overflow-hidden rounded-lg border border-border bg-card pl-0 shadow-sm transition-colors",
        task.skipped && "opacity-60",
        // 소스(원본): 드래그 중엔 흐리게 자리만 유지. 들어올림 시각은 DragOverlay 클론이 담당.
        isDragging && "opacity-40",
        // 떠 있는 클론(DragOverlay 안): 살짝 기울이고 그림자/링으로 들어올림 표현.
        overlay &&
          "rotate-2 cursor-grabbing shadow-xl ring-2 ring-amber-400/70",
        canDrag && !overlay && "cursor-grab touch-none active:cursor-grabbing",
      )}
      {...dragHandleProps}
    >
      {/* plan 색 좌측 스트라이프 — 드래그 가능 힌트로 hover 시 살짝 두꺼워짐 */}
      <span
        className={cn(
          "w-1 shrink-0 transition-[width]",
          PLAN_STRIPE[task.planId],
          canDrag && "group-hover/card:w-1.5",
        )}
        aria-hidden
      />

      {inline ? (
        // 단일 행(인라인): [체크박스] [이모지] 제목(flex-1 1줄 truncate) ……… [출석] [⋯].
        // 제목이 남은 폭을 모두 먹고 출석 뱃지·메뉴는 끝에 붙는다. 한 줄 컴팩트 높이.
        <div className="flex min-w-0 flex-1 items-center gap-1.5 py-1.5 pl-1 pr-1.5">
          {checkbox}
          {emoji}
          {langBadge}
          {titleLink}
          {attendanceBadge}
          {menu && <div className="shrink-0">{menu}</div>}
        </div>
      ) : (
        // 기본(title-hero) 2단: 컨트롤 행 + 제목 hero 행.
        <div className="flex min-w-0 flex-1 flex-col gap-1 py-1.5 pl-1 pr-1.5">
          {/* 컨트롤 행: 체크박스 + 이모지 + 언어 뱃지 (좌) / ⋯ 메뉴 (우). 제목 폭을 잠식하지 않게 고정 높이.
              언어 뱃지를 컨트롤 행에 두어 제목 hero 의 3줄 줄바꿈(line-clamp-3)을 건드리지 않는다. */}
          <div className="flex items-center gap-1.5">
            {checkbox}
            {emoji}
            {langBadge}

            {/* 출석 완료 뱃지(컨트롤 행 우측, 메뉴 옆) */}
            {showAttendance && <div className="ml-auto">{attendanceBadge}</div>}

            {/* ⋯ 메뉴 — 드래그의 키보드/모바일 대안. 출석 뱃지가 없을 땐 ml-auto 로 우측 정렬. */}
            {menu && (
              <div className={cn("shrink-0", !showAttendance && "ml-auto")}>
                {menu}
              </div>
            )}
          </div>

          {/* 제목 행(hero): 카드 전체 폭, 한글 단어 단위 줄바꿈(break-keep), 최대 3줄. truncate 없음. */}
          {titleLink}
        </div>
      )}
    </div>
  );
}

/**
 * 메뉴(트리거·portal 콘텐츠·하위 메뉴)의 pointerdown 이 React 트리를 타고 카드 드래그 리스너와
 * 보드 스와이프까지 올라가지 않게 막는다. 날짜를 누른 채 조금만 움직여도 카드 드래그가 시작되거나
 * 보드가 포인터를 캡처해 항목 선택이 무시됐다(JCG-4019). Radix 내부 핸들러는 그대로 실행된다.
 */
function stopMenuPointerDown(e: ReactPointerEvent) {
  e.stopPropagation();
}

function TaskMenu({
  task,
  actions,
  moveTargets,
  moved,
}: {
  task: ScheduledTask;
  actions: TaskCardActions;
  moveTargets: MoveTarget[];
  moved: boolean;
}) {
  // 같은 제목의 언어·회독을 스크린리더와 메뉴에서도 구분한다.
  const langLabel = codeLangLabel(task.codeLang);
  const titleWithDetails = [
    task.reviewRound ? `${task.reviewRound}회독` : "",
    task.title,
    langLabel,
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        onPointerDown={stopMenuPointerDown}
        className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
        aria-label={`${titleWithDetails} 일정 옵션`}
      >
        <MoreHorizontal className="h-4 w-4" aria-hidden />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-52"
        onPointerDown={stopMenuPointerDown}
      >
        <DropdownMenuLabel className="truncate text-xs">
          {STUDY_PLAN_LABELS[task.planId]} · {titleWithDetails}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        <DropdownMenuItem
          onSelect={() => actions.onToggle(task, !task.done)}
          disabled={task.skipped}
        >
          {task.done ? "완료 해제" : "완료로 표시"}
        </DropdownMenuItem>

        {moveTargets.length > 0 && (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>다른 날로 이동</DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="max-h-72 overflow-y-auto">
              {moveTargets.map((target) => (
                <DropdownMenuItem
                  key={target.iso}
                  disabled={target.iso === task.effectiveDateISO}
                  onSelect={() => actions.onMove(task, target.iso)}
                >
                  <span className="flex-1">
                    {formatMonthDayWeekday(target.iso)}
                  </span>
                  {target.isToday && (
                    <span className="text-[10px] font-semibold text-amber-600 dark:text-amber-400">
                      오늘
                    </span>
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}

        {moved && (
          <DropdownMenuItem onSelect={() => actions.onMove(task, null)}>
            {task.completionDrivesDate
              ? "완료한 날짜 되돌리기"
              : "정규 날짜로 되돌리기"}
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => actions.onSkip(task, !task.skipped)}>
          {task.skipped ? "건너뛰기 해제" : "이 학습 건너뛰기"}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
